import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PageLoader } from '@lex/ui';
import type { CaseStage } from '@lex/contracts';
import { apiClient } from '../lib/api';

const STAGES: Array<{ value: CaseStage; label: string }> = [
  { value: 'accepted', label: 'Принято' },
  { value: 'documents', label: 'Документы' },
  { value: 'draft', label: 'Черновик' },
  { value: 'filed', label: 'Подано' },
  { value: 'response', label: 'Ответ' },
  { value: 'completed', label: 'Завершено' },
];

function dateLabel(iso: string | null): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('ru-RU', {
    timeZone: 'Europe/Moscow',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
    .format(new Date(iso))
    .replace(' г.', '');
}

export function CaseDetailPage() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const detail = useQuery({ queryKey: ['case', id], queryFn: () => apiClient.chat.myCase(id) });
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setComment('');
  }, [id]);

  if (detail.isLoading) return <PageLoader />;
  if (!detail.data) {
    return (
      <div className="mx-auto max-w-3xl rounded-3xl border border-line bg-white/90 p-8">
        <p className="font-semibold text-ink">Дело не найдено</p>
        <Link to="/cases" className="mt-3 inline-block text-sm font-semibold text-accent">
          ← К делам
        </Link>
      </div>
    );
  }

  const value = detail.data;
  const current = STAGES.findIndex((stage) => stage.value === value.stage);
  const latest = value.draftVersions.at(-1);

  const sendComment = async (): Promise<void> => {
    if (!comment.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await apiClient.chat.commentCaseDraft(id, { text: comment.trim() });
      setComment('');
      await queryClient.invalidateQueries({ queryKey: ['case', id] });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось отправить правки');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-5xl space-y-4 pb-10">
      <div>
        <Link to="/cases" className="text-[14px] font-semibold text-accent">
          ← Дела
        </Link>
        <h1 className="mt-3 text-[30px] font-semibold leading-tight tracking-[-0.02em] text-ink md:text-[40px]">
          {value.title}
        </h1>
        <p className="mt-1 text-[13.5px] text-muted">
          {value.lawyerName ?? 'Юрист не назначен'} · создано {dateLabel(value.createdAt)}
        </p>
      </div>

      <section className="rounded-3xl border border-line bg-white/90 p-4 md:p-5">
        <div className="flex flex-wrap gap-1.5">
          {STAGES.map((stage, index) => (
            <span
              key={stage.value}
              className={`rounded-xl px-2.5 py-1.5 text-[12px] font-semibold ${
                index === current
                  ? 'bg-accent text-white'
                  : index < current
                    ? 'bg-success-soft text-success'
                    : 'bg-soft text-muted'
              }`}
            >
              {stage.label}
            </span>
          ))}
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <section className="rounded-3xl border border-line bg-white/90 p-5">
          <h2 className="text-[18px] font-semibold text-ink">Черновик жалобы</h2>
          {latest ? (
            <>
              <pre className="mt-3 max-h-[420px] overflow-y-auto whitespace-pre-wrap rounded-2xl bg-soft px-4 py-4 font-sans text-[14px] leading-6 text-ink">
                {latest.body}
              </pre>
              <p className="mt-2 text-[11.5px] text-muted">
                Версия {latest.version} · {latest.authorName ?? 'юрист'} ·{' '}
                {dateLabel(latest.createdAt)}
              </p>
            </>
          ) : (
            <p className="mt-2 text-[13.5px] text-muted">
              Юрист ещё готовит черновик. Здесь появится текст с возможностью оставить правки.
            </p>
          )}

          {latest ? (
            <div className="mt-4 border-t border-line pt-4">
              <h3 className="text-[15px] font-semibold text-ink">Ваши правки</h3>
              {value.clientDraftComment ? (
                <p className="mt-2 whitespace-pre-wrap rounded-xl bg-accent-soft px-3.5 py-3 text-[13.5px] leading-5 text-ink">
                  {value.clientDraftComment}
                </p>
              ) : null}
              <textarea
                value={comment}
                onChange={(event) => setComment(event.target.value)}
                rows={3}
                placeholder="Что поправить в черновике?"
                className="mt-3 w-full resize-none rounded-2xl border border-line bg-white px-4 py-3 text-[14px] text-ink outline-none placeholder:text-muted-400"
              />
              {error ? <p className="mt-1 text-[12px] text-danger">{error}</p> : null}
              <button
                type="button"
                disabled={busy || !comment.trim()}
                onClick={() => void sendComment()}
                className="mt-2 rounded-xl bg-accent px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
              >
                Отправить правки
              </button>
            </div>
          ) : null}
        </section>

        <aside className="space-y-4">
          <section className="rounded-3xl border border-line bg-white/90 p-5">
            <h2 className="text-[16px] font-semibold text-ink">Сроки</h2>
            <dl className="mt-3 space-y-2 text-[13.5px]">
              <div className="flex justify-between gap-3">
                <dt className="text-muted">Срок ответа</dt>
                <dd className="font-medium text-ink">{dateLabel(value.responseDueAt)}</dd>
              </div>
              <div className="flex justify-between gap-3">
                <dt className="text-muted">Срок обжалования</dt>
                <dd className="font-medium text-ink">{dateLabel(value.appealUntil)}</dd>
              </div>
            </dl>
          </section>

          <section className="rounded-3xl border border-line bg-white/90 p-5">
            <h2 className="text-[16px] font-semibold text-ink">Нужна помощь?</h2>
            <p className="mt-1 text-[13px] leading-5 text-muted">
              Напишите юристу в чате — он увидит ваши правки и ответит по делу.
            </p>
            <Link
              to={`/chat?conversation=${value.conversationId}`}
              className="mt-3 inline-flex rounded-xl bg-ink-gradient px-4 py-2.5 text-sm font-semibold text-white"
            >
              Открыть чат
            </Link>
          </section>
        </aside>
      </div>
    </div>
  );
}
